import { DislikeOutlined, LikeOutlined, ReloadOutlined } from "@ant-design/icons";
import type React from "react";
import { useEffect, useId, useState } from "react";
import { type PrechartRefreshResult, prechartBaseUrl, requestPrechartRefresh, sendPrechartFeedback } from "../prechart";

// The pre-visit brief: what changed since the patient's last visit, composed
// before the clinician opens the chart (backend `prechart/`). Posted into the
// thread by the `pre_visit_brief` custom action and restored from thread
// metadata anchored to its lead-in message. Payload shape:
// docs/precharting/api-contract.md (BriefPayload v3, milestone 1).
//
// Top to bottom: header, Quick summary (`summary_line`, `flags`, then a quiet
// line for any document that may be another patient's), "Since your <date>
// visit" (the last visit as the anchor row with its note's first sentence, then
// `events` by date, newest day first, three days with more than folds shown),
// Worth asking (`questions`), what was not read, actions.
// The flags and the timeline are built from the facts; only the summary line,
// the note summary and the questions are the model's, and they carry the AI
// mark. A card at
// least 720px wide lays out in two columns: the summary and the questions on
// the left, the whole timeline on the right.
//
// Read-only apart from three calls that never touch the chat stream: rows and
// source chips open their evidence in place, Refresh queues a fresh
// pre-chart, and thumbs up/down store feedback on this brief version.
export interface BriefSource {
  // The fact id ("result:<id>", "doc:<id>", "enc:<id>", "outside:<key>",
  // "outside-proc:<key>", "note:<audiofile id>"), as the harness's Source.ref.
  ref: string;
  kind?: "result" | "document" | "encounter" | "outside_result" | "outside_procedure" | "note" | string;
  label: string;
  unreviewed?: boolean;
  detail?: string | null;
}

// v1 sections and the v2 headline: still in the payload for Superdoc's context
// renderer. The card shows the headline only for a payload that has nothing
// else (no events, flags or last visit).
export interface BriefItem {
  text: string;
  priority?: "high" | "open" | "normal" | string;
  sources?: BriefSource[];
  ai?: boolean;
}

export interface BriefSection {
  key: string;
  title: string;
  items?: BriefItem[];
}

export interface BriefCoverage {
  complete?: boolean;
  sources?: Array<{ source: string; status: string; count?: number; detail?: string }>;
  gaps?: string[];
}

export interface BriefAnchor {
  date?: string | null;
  visit_type?: string | null;
  days_ago?: number | null;
  rule?: string | null;
  // "provider": this clinician's own last office visit; "practice": the practice's.
  scope?: "provider" | "practice" | string | null;
  note_signed?: boolean | null;
  // The bundle capped a very old anchor to its maximum window, which spans
  // `window_days` (days from the window start to the appointment).
  window_capped?: boolean | null;
  window_days?: number | null;
}

// One fact (or a fold of many) on the timeline.
export interface BriefEvent {
  ref: string;
  kind:
    | "result"
    | "pending"
    | "document"
    | "telephone"
    | "office"
    | "other_visit"
    | "outside_result"
    | "outside_procedure"
    | "fold"
    | string;
  date?: string | null;
  end_date?: string | null;
  arrived?: string | null;
  title: string;
  // result and pending: the order's name.
  name?: string | null;
  sub?: string | null;
  flagged?: boolean;
  unreviewed?: boolean;
  other_provider?: boolean;
  older_care?: boolean;
  open?: boolean;
  read?: boolean;
  identity_mismatch?: boolean;
  count?: number | null;
  sources?: BriefSource[];
}

// A Quick summary row (deterministic): the bold `headline` from the EHR's own
// fields, then the quiet `detail`. An open order has only the detail.
export interface BriefFlag {
  ref: string;
  kind: "document" | "result" | "outside_result" | "outside_procedure" | "pending" | string;
  date?: string | null;
  priority?: "high" | "open" | string;
  headline?: string | null;
  detail?: string | null;
  unreviewed?: boolean;
  outside?: boolean;
  sources?: BriefSource[];
}

// The prior note the morning pull imported for this patient.
export interface BriefPriorNote {
  available?: boolean;
  source?: string | null;
  service_date?: string | null;
  // False: the newest note on file is not the anchor visit's.
  matches_anchor?: boolean | null;
  // The model's, from the note text (empty without a model or a note).
  plan_phrases?: string[] | null;
  summary?: string | null;
  ai?: boolean;
}

// The anchor visit as the timeline's first row.
export interface BriefLastVisit {
  date?: string | null;
  visit_type?: string | null;
  note_signed?: boolean | null;
  mine?: boolean | null;
  orders_text?: string | null;
  order_names?: string[] | null;
  open_names?: string[] | null;
  note?: BriefPriorNote | null;
  sources?: BriefSource[];
}

export interface BriefQuestion {
  text: string;
  ai?: boolean;
  sources?: BriefSource[];
}

// The model's one-line read of the flags, above them.
export interface BriefSummaryLine {
  text: string;
  ai?: boolean;
}

export interface PreVisitBriefData {
  brief_id: string;
  run_id?: string;
  version?: number;
  status?: string;
  patient_name?: string;
  appointment?: { date?: string | null; time?: string | null; visit_type?: string | null } | null;
  anchor?: BriefAnchor | null;
  is_new_patient?: boolean;
  headline?: string | null;
  composer?: "llm" | "deterministic" | string;
  summary_line?: BriefSummaryLine | null;
  flags?: BriefFlag[] | null;
  // The count before the cap; more than shown ⇒ "+N more in the timeline".
  flags_total?: number | null;
  last_visit?: BriefLastVisit | null;
  questions?: BriefQuestion[] | null;
  events?: BriefEvent[] | null;
  coverage?: BriefCoverage | null;
  sections?: BriefSection[];
  generated_at?: string;
}

interface PreVisitBriefProps {
  data: PreVisitBriefData;
  // The patient the panel is open on — Refresh queues a pre-chart for them.
  // Absent (no patient selected) ⇒ no Refresh.
  clientId?: number;
  // A newer brief is further down the thread: read-only, no actions.
  frozen?: boolean;
}

// "2026-09-30" as a local calendar date (never shifted by the timezone).
const parseDay = (iso?: string | null): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

// "Wed, Sep 30"
export const appointmentDayLabel = (iso?: string | null): string => {
  const d = parseDay(iso);
  return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : iso || "";
};

// "Jul 5", with the year when it isn't the reference year ("Jul 5, 2025").
export const lastVisitDayLabel = (iso?: string | null, referenceYear?: number): string => {
  const d = parseDay(iso);
  if (!d) return iso || "";
  const year = referenceYear ?? new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== year ? { year: "numeric" } : {}),
  });
};

export const daysAgoLabel = (days?: number | null): string => {
  if (days == null || Number.isNaN(days)) return "";
  if (days <= 0) return "today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

// "2026-09-12" → "Sep 12" (with the year when it isn't the visit's) wherever the
// backend wrote an ISO date into text: it keeps ISO so Superdoc's context stays
// unambiguous; the card reads dates the way its header does.
const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;
const prettyDates = (text: string, referenceYear?: number): string =>
  text.replace(ISO_DATE, (iso) => lastVisitDayLabel(iso, referenceYear) || iso);

// "2026-09-30" out of a date or datetime, "" without one: compares and sorts as text.
const dayKey = (iso?: string | null): string => /^\d{4}-\d{2}-\d{2}/.exec((iso || "").trim())?.[0] ?? "";

// A date column: "Sep 14", then the year on its own line when it isn't the visit's.
const dateLines = (iso?: string | null, referenceYear?: number): string[] => {
  const d = parseDay(iso);
  if (!d) return [];
  const lines = [d.toLocaleDateString("en-US", { month: "short", day: "numeric" })];
  if (referenceYear != null && d.getFullYear() !== referenceYear) lines.push(String(d.getFullYear()));
  return lines;
};

// A sentence ends at . ! or ? before a space or the end of the text, except
// after the abbreviations a clinical note uses mid-sentence.
const ABBREVIATION = /(?:^|[\s(])(?:Dr|Mr|Mrs|e\.g)\.$/i;

// "One. Two. Three." → ["One.", "Two. Three."]; one sentence → [text, ""].
export const splitFirstSentence = (text: string): [string, string] => {
  const end = /[.!?](?=\s|$)/g;
  for (let m = end.exec(text); m; m = end.exec(text)) {
    const head = text.slice(0, m.index + 1);
    if (!ABBREVIATION.test(head)) return [head.trim(), text.slice(m.index + 1).trim()];
  }
  return [text.trim(), ""];
};

const withLabel = (sources?: BriefSource[] | null): BriefSource[] => (sources ?? []).filter((s) => !!s?.label?.trim());

const AI_MARK_TITLE = "Model summary, checked against the chart";
const MAX_CHIPS = 4;
const MAX_FLAGS = 5;
const MAX_QUESTIONS = 3;
const PREVIEW_DAYS = 3;
// The card's own width (border box) from which it lays out in two columns.
const WIDE_MIN_PX = 720;
const OUTSIDE_KINDS = ["outside_result", "outside_procedure", "outside"];
// Where each row's text starts, so what a row opens lines up under it:
// flags = date 44 + dot 12 + glyph 14 + three 6px gaps; events = glyph 14 + 7;
// questions = glyph 14 + 8. A quiet line under the flags puts its glyph in
// the flags' glyph column (date 44 + dot 12 + two gaps).
const FLAG_TEXT_INDENT = "88px";
const FLAG_GLYPH_INDENT = "68px";
const EVENT_TEXT_INDENT = "21px";
const QUESTION_TEXT_INDENT = "22px";

const ClipboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="3" y="2.5" width="10" height="11.5" rx="1.5" stroke="#6366F1" strokeWidth="1.3" />
    <path d="M6 2.5h4v1.5H6z" fill="#EEF2FF" stroke="#6366F1" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 7h5M5.5 9.5h5M5.5 12h3" stroke="#6366F1" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// Marks the model's text (the note summary, the questions) apart from the facts.
const AiMark: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <span role="img" aria-label={AI_MARK_TITLE} title={AI_MARK_TITLE} style={{ ...styles.aiMark, ...style }}>
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" style={{ display: "block" }}>
      <path
        d="M6 .8c.3 2.6 1.6 3.9 4.2 4.2C7.6 5.3 6.3 6.6 6 9.2 5.7 6.6 4.4 5.3 1.8 5 4.4 4.7 5.7 3.4 6 .8z"
        fill="currentColor"
      />
      <path
        d="M9.7 8.3c.1.8.4 1.1 1.2 1.2-.8.1-1.1.4-1.2 1.2-.1-.8-.4-1.1-1.2-1.2.8-.1 1.1-.4 1.2-1.2z"
        fill="currentColor"
      />
    </svg>
  </span>
);

// One small glyph per kind of fact (stroke = currentColor).
type GlyphName =
  | "result"
  | "document"
  | "telephone"
  | "office"
  | "outside"
  | "pending"
  | "fold"
  | "warning"
  | "flag"
  | "ask";

const GLYPHS: Record<GlyphName, React.ReactNode> = {
  result: (
    <path
      d="M5.5 2h3M6 2v4.2L3.2 11a1 1 0 0 0 .9 1.5h5.8a1 1 0 0 0 .9-1.5L8 6.2V2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  document: (
    <>
      <path
        d="M4 1.5h4l3 3V12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2.5a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path d="M5 8h4M5 10h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  telephone: (
    <path
      d="M3.2 2.2 5 2l1.2 2.8-1.3 1a6 6 0 0 0 3.3 3.3l1-1.3L12 9l-.2 1.8c-.1.8-.8 1.3-1.6 1.2A9.5 9.5 0 0 1 2 3.8c-.1-.8.4-1.5 1.2-1.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  office: (
    <>
      <circle cx="7" cy="4.5" r="2.3" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M2.5 12.5c.4-2.6 2.2-4 4.5-4s4.1 1.4 4.5 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </>
  ),
  outside: (
    <path
      d="M2 12.5h10M3 12.5V4l4-2.5L11 4v8.5M5.5 6.5h3M5.5 9h3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  pending: (
    <>
      <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M7 4v3.2l2 1.3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </>
  ),
  fold: (
    <path
      d="M2.5 5.5 7 3l4.5 2.5L7 8z M2.5 8 7 10.5 11.5 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
  warning: (
    <>
      <path
        d="M6.5 2.3a.6.6 0 0 1 1 0l5.3 9.4a.6.6 0 0 1-.5.9H1.7a.6.6 0 0 1-.5-.9z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M7 5.8v2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="7" cy="10.6" r=".7" fill="currentColor" />
    </>
  ),
  flag: (
    <>
      <path d="M3.5 12V2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M4 3h6.6L9.2 5.3l1.4 2.3H4z" fill="currentColor" />
    </>
  ),
  ask: (
    <path
      d="M2 3.5A1.5 1.5 0 0 1 3.5 2h7A1.5 1.5 0 0 1 12 3.5v5A1.5 1.5 0 0 1 10.5 10H6l-3 2.5V10A1.5 1.5 0 0 1 2 8.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  ),
};

const GLYPH_COLOR: Record<GlyphName, string> = {
  result: "#6366F1",
  document: "#6366F1",
  telephone: "#6B7280",
  office: "#6B7280",
  outside: "#0F766E",
  pending: "#B45309",
  fold: "#9CA3AF",
  warning: "#B45309",
  flag: "#B45309",
  ask: "#4B5563",
};

const KIND_GLYPH: Record<string, GlyphName> = {
  result: "result",
  pending: "pending",
  document: "document",
  telephone: "telephone",
  office: "office",
  other_visit: "office",
  outside: "outside",
  outside_result: "outside",
  outside_procedure: "outside",
  fold: "fold",
};

// Sub-lines that only name the row's kind, which its glyph already shows.
const KIND_ONLY_SUB: Record<string, string> = {
  telephone: "telephone encounter",
  office: "office visit",
  other_visit: "office visit",
};

const subLine = (e: BriefEvent): string => {
  const sub = e.sub?.trim() ?? "";
  return sub.toLowerCase() === KIND_ONLY_SUB[e.kind] ? "" : sub;
};

const Glyph: React.FC<{ name: GlyphName }> = ({ name }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    aria-hidden="true"
    focusable="false"
    style={{ ...styles.glyph, color: GLYPH_COLOR[name] }}
  >
    {GLYPHS[name]}
  </svg>
);

const Tag: React.FC<{ tone: React.CSSProperties; children: React.ReactNode }> = ({ tone, children }) => (
  <span style={{ ...styles.tag, ...tone }}>{children}</span>
);

// Tags sit inline after a row's text and wrap with it.
const Tags: React.FC<{ tags: React.ReactNode[] }> = ({ tags }) =>
  tags.length ? <span style={styles.tags}>{tags}</span> : null;

// A date column; spans, so it may sit inside a row's button.
const DateCell: React.FC<{ lines: string[]; style: React.CSSProperties }> = ({ lines, style }) => (
  <span style={style}>
    {lines.map((line, i) => (
      <span key={i} style={{ display: "block" }}>
        {line}
      </span>
    ))}
  </span>
);

// One source chip. A chip with detail is a toggle button; one without is a
// plain label (nothing to expand, so nothing to click). The Unreviewed badge
// sits beside its chip and wraps under it in a narrow panel, so the label
// stays readable.
const SourceChip: React.FC<{
  source: BriefSource;
  expanded: boolean;
  detailId: string;
  onToggle: () => void;
  badge: boolean;
  year?: number;
}> = ({ source, expanded, detailId, onToggle, badge, year }) => {
  const label = <span style={styles.chipLabel}>{prettyDates(source.label, year)}</span>;
  const chip = source.detail?.trim() ? (
    <button
      type="button"
      style={{ ...styles.chip, ...styles.chipButton, ...(expanded ? styles.chipExpanded : {}) }}
      aria-expanded={expanded}
      aria-controls={detailId}
      onClick={onToggle}
    >
      {label}
    </button>
  ) : (
    <span style={styles.chip}>{label}</span>
  );
  if (!badge || !source.unreviewed) return chip;
  return (
    <span style={styles.chipGroup}>
      {chip}
      <Tag tone={styles.tagUnreviewed}>Unreviewed</Tag>
    </span>
  );
};

interface ChipState {
  idPrefix: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  // The visit's year: dates in that year show without it.
  year?: number;
}

// A line's sources. `badges` false: the row carries its own Unreviewed tag.
const SourceChips: React.FC<ChipState & { sources: BriefSource[]; keyPrefix: string; badges?: boolean }> = ({
  sources,
  keyPrefix,
  badges = true,
  idPrefix,
  expanded,
  onToggle,
  year,
}) => {
  const [showAll, setShowAll] = useState(false);
  if (!sources.length) return null;
  // A backlog line can cite dozens of documents; keep the row to one glance.
  const shown = showAll ? sources : sources.slice(0, MAX_CHIPS);
  const open = shown
    .map((s, i) => ({ s, key: `${keyPrefix}:${i}` }))
    .filter(({ s, key }) => expanded.has(key) && !!s.detail?.trim());
  return (
    <>
      <div style={styles.chipRow}>
        {shown.map((s, i) => {
          const key = `${keyPrefix}:${i}`;
          return (
            <SourceChip
              key={key}
              source={s}
              expanded={expanded.has(key)}
              detailId={`${idPrefix}-${key}`}
              onToggle={() => onToggle(key)}
              badge={badges}
              year={year}
            />
          );
        })}
        {sources.length > shown.length && (
          <button type="button" style={{ ...styles.chip, ...styles.chipButton }} onClick={() => setShowAll(true)}>
            +{sources.length - shown.length} more
          </button>
        )}
      </div>
      {open.map(({ s, key }) => (
        <div key={key} id={`${idPrefix}-${key}`} style={styles.detail}>
          <span style={styles.detailLabel}>{prettyDates(s.label, year)}:</span> {prettyDates(s.detail ?? "", year)}
        </div>
      ))}
    </>
  );
};

// A row (flag, timeline entry, question) that opens its evidence beneath it:
// a sub line and the sources' chips, never shown by default. With nothing to
// open it is a plain row, not a button.
const OpenableRow: React.FC<{
  rowKey: string;
  // The row's own grid.
  rowStyle: React.CSSProperties;
  // Where the row's text starts, so what opens lines up under it.
  indent: string;
  sources: BriefSource[];
  sub?: string;
  badges?: boolean;
  chips: ChipState;
  onOpen: (key: string, sources: BriefSource[]) => void;
  children: React.ReactNode;
}> = ({ rowKey, rowStyle, indent, sources, sub, badges = false, chips, onOpen, children }) => {
  if (!sources.length && !sub) return <div style={rowStyle}>{children}</div>;
  const open = chips.expanded.has(rowKey);
  const regionId = `${chips.idPrefix}-${rowKey}`;
  return (
    <>
      <button
        type="button"
        style={styles.rowButton}
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => onOpen(rowKey, sources)}
      >
        <span style={rowStyle}>{children}</span>
      </button>
      {open && (
        <div id={regionId} style={{ ...styles.rowOpen, paddingLeft: indent }}>
          {sub && <div style={styles.rowSub}>{sub}</div>}
          <SourceChips sources={sources} keyPrefix={rowKey} badges={badges} {...chips} />
        </div>
      )}
    </>
  );
};

// The card's own width, measured, so it lays out for wherever it is mounted
// (the Superdoc panel, a wide view). Without ResizeObserver: one column.
const useWide = (el: HTMLElement | null): boolean => {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      // Border box: the card's padding changes with the layout; its width doesn't.
      const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      setWide(width >= WIDE_MIN_PX);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return wide;
};

type EventRow = { event: BriefEvent; index: number };
type DayGroup = { day: string; rows: EventRow[] };

// Within a day: what happened (visits, calls), then what arrived (documents,
// results, outside records), then what is still open, then folds.
const KIND_ORDER: Record<string, number> = {
  office: 0,
  other_visit: 0,
  telephone: 0,
  document: 1,
  result: 2,
  outside_result: 3,
  outside_procedure: 3,
  pending: 4,
  fold: 5,
};
const kindRank = (e: BriefEvent): number => KIND_ORDER[e.kind] ?? 2;

// Rows by day, newest day first, undated last; by kind within a day.
const byDay = (rows: EventRow[]): DayGroup[] => {
  const days = new Map<string, EventRow[]>();
  for (const row of rows) {
    const day = dayKey(row.event.date);
    const list = days.get(day);
    if (list) list.push(row);
    else days.set(day, [row]);
  }
  return Array.from(days.entries())
    .sort(([a], [b]) => (!a ? 1 : !b ? -1 : b.localeCompare(a)))
    .map(([day, list]) => ({
      day,
      rows: list.slice().sort((x, y) => kindRank(x.event) - kindRank(y.event)),
    }));
};

type FeedbackState = { phase: "idle" | "sending" | "sent" | "error"; helpful?: boolean };

const PreVisitBrief: React.FC<PreVisitBriefProps> = ({ data, clientId, frozen }) => {
  const idPrefix = useId();
  const [card, setCard] = useState<HTMLElement | null>(null);
  const wide = useWide(card);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [allDays, setAllDays] = useState(false);
  // null: the default for the layout (open in the wide view, closed otherwise).
  const [noteOpen, setNoteOpen] = useState<boolean | null>(null);
  const [refresh, setRefresh] = useState<"idle" | "sending" | PrechartRefreshResult>("idle");
  const [feedback, setFeedback] = useState<FeedbackState>({ phase: "idle" });

  if (!data?.brief_id) return null;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // A row opens its sources (a lone source opens its detail with it) and
  // closes them again, details included.
  const toggleRow = (key: string, sources: BriefSource[]) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (prev.has(key)) {
        prev.forEach((k) => {
          if (k === key || k.startsWith(`${key}:`)) next.delete(k);
        });
      } else {
        next.add(key);
        if (sources.length === 1 && sources[0].detail?.trim()) next.add(`${key}:0`);
      }
      return next;
    });

  const appt = data.appointment;
  const anchor = data.anchor;
  const referenceYear = parseDay(appt?.date)?.getFullYear();
  const chips: ChipState = { idPrefix, expanded, onToggle: toggle, year: referenceYear };
  const pretty = (text?: string | null) => prettyDates(text ?? "", referenceYear);
  const dayLabel = (iso?: string | null) => lastVisitDayLabel(iso, referenceYear);
  const yourVisit = anchor?.scope === "provider";
  const aiComposed = data.composer !== "deterministic";

  // ── Header ──
  const apptLine = appt ? [appointmentDayLabel(appt.date), appt.time, appt.visit_type].filter(Boolean).join(" · ") : "";
  const windowDays = anchor?.window_days ?? 0;
  const windowNote = !anchor?.window_capped
    ? ""
    : windowDays > 0
      ? `showing the last ${windowDays} days`
      : "showing a shorter window";
  // The anchor is this clinician's own visits, so "new" means new to them,
  // not to the practice.
  const lastVisitLine = data.is_new_patient
    ? "No earlier visit with you on record"
    : anchor?.date
      ? [
          `${yourVisit ? "Your last visit" : "Last visit"} ${dayLabel(anchor.date)}`,
          anchor.visit_type,
          daysAgoLabel(anchor.days_ago),
          anchor.note_signed === false ? "note not signed" : "",
          windowNote,
        ]
          .filter(Boolean)
          .join(" · ")
      : "";

  // Every event with a title, in payload order (newest first).
  const rows: EventRow[] = (data.events ?? [])
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => !!event?.title?.trim());

  // ── Quick summary: the flags (deterministic), every missing array read as empty ──
  const flags = (data.flags ?? []).filter((f) => !!(f?.headline?.trim() || f?.detail?.trim())).slice(0, MAX_FLAGS);
  const moreFlags = (data.flags_total ?? 0) - flags.length;

  const renderFlag = (flag: BriefFlag, i: number) => {
    const key = `f${i}`;
    const priority = flag.priority === "high" || flag.priority === "open" ? flag.priority : "normal";
    const headline = pretty(flag.headline).trim();
    const detail = pretty(flag.detail).trim();
    const tags = [
      flag.unreviewed && (
        <Tag key="unreviewed" tone={styles.tagUnreviewed}>
          Unreviewed
        </Tag>
      ),
      flag.outside && (
        <Tag key="prisma" tone={styles.tagTeal}>
          Prisma
        </Tag>
      ),
    ].filter(Boolean);
    return (
      <li key={key} data-priority={priority}>
        <OpenableRow
          rowKey={key}
          rowStyle={styles.flagRow}
          indent={FLAG_TEXT_INDENT}
          sources={withLabel(flag.sources)}
          chips={chips}
          onOpen={toggleRow}
        >
          <DateCell lines={dateLines(flag.date, referenceYear)} style={styles.flagDate} />
          <span style={styles.flagDotCell} aria-hidden="true">
            <span
              style={{
                ...styles.flagDot,
                ...(priority === "high" ? styles.flagDotHigh : priority === "open" ? styles.flagDotOpen : {}),
              }}
            />
          </span>
          <Glyph name={KIND_GLYPH[flag.kind] ?? "document"} />
          <span style={styles.rowText}>
            {priority === "high" && <span style={styles.srOnly}>Flagged: </span>}
            {headline && <b style={styles.flagHeadline}>{headline}</b>}
            {headline && detail ? " · " : ""}
            {detail}
            <Tags tags={tags} />
          </span>
        </OpenableRow>
      </li>
    );
  };

  // A document that may be another patient's is not a flag (decision
  // 2026-10-04), but it must be read: one quiet line under the flags.
  const identityLines = rows
    .filter(({ event }) => event.identity_mismatch)
    .map(({ event, index }) => {
      const day = dayLabel(dayKey(event.date));
      return {
        key: `id${index}`,
        // The title alone: a document's row title carries its summary after " · ".
        text: `${pretty(event.title).split(" · ")[0].trim()}${day ? ` (${day})` : ""} may belong to another patient — check before relying on it.`,
      };
    });

  const summaryLine = pretty(data.summary_line?.text).trim();

  const quickSummary =
    data.flags || summaryLine ? (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Quick summary</div>
        {summaryLine && (
          <div style={styles.summaryLine}>
            {data.summary_line?.ai !== false && <AiMark />}
            {summaryLine}
          </div>
        )}
        {flags.length ? (
          <ul style={styles.flagList}>{flags.map(renderFlag)}</ul>
        ) : (
          data.flags && <div style={styles.quietLine}>Nothing flagged.</div>
        )}
        {identityLines.length > 0 && (
          <ul style={styles.identityList}>
            {identityLines.map(({ key, text }) => (
              <li key={key} style={styles.identityLine}>
                <Glyph name="flag" />
                <span style={styles.rowText}>{text}</span>
              </li>
            ))}
          </ul>
        )}
        {moreFlags > 0 && <div style={styles.moreFlags}>+{moreFlags} more in the timeline</div>}
      </div>
    ) : null;

  // ── The last visit: the timeline's anchor row ──
  const lastVisit = data.last_visit;
  const note = lastVisit?.note;
  const noteElsewhere = !!note?.available && note.matches_anchor === false;
  const visitLabel = noteElsewhere
    ? ["Last note on file", dayLabel(note?.service_date)].filter(Boolean).join(" · ")
    : [lastVisit?.mine ?? yourVisit ? "Your visit" : "Last visit", lastVisit?.visit_type].filter(Boolean).join(" · ");
  const visitTail = [
    (note?.plan_phrases ?? [])
      .map((p) => pretty(p).trim())
      .filter(Boolean)
      .join("; "),
    pretty(lastVisit?.orders_text).trim(),
  ].filter(Boolean);
  // The note summary's first sentence reads under the row; "note ›" opens the
  // rest and the note's sources (only the sources for a one-sentence summary).
  const [noteLead, noteRest] = splitFirstSentence(pretty(note?.summary));
  const noteSources = withLabel(lastVisit?.sources);
  const noteMore = !!noteLead && (!!noteRest || noteSources.length > 0);
  const noteShown = noteMore && (noteOpen ?? wide);
  const noteId = `${idPrefix}-note`;

  const anchorRow = lastVisit ? (
    <div style={styles.anchor} data-anchor="last-visit">
      <div style={styles.anchorHead}>
        {(lastVisit.date || anchor?.date) && (
          <span style={styles.anchorDate}>{dayLabel(lastVisit.date || anchor?.date)}</span>
        )}
        <span style={styles.anchorTitle}>{visitLabel}</span>
        {visitTail.length > 0 && <span style={styles.anchorTail}>{` · ${visitTail.join(" · ")}`}</span>}
        {noteMore && (
          <button
            type="button"
            style={styles.anchorToggle}
            aria-label="Visit note summary"
            aria-expanded={noteShown}
            aria-controls={noteId}
            onClick={() => setNoteOpen(!noteShown)}
          >
            {noteShown ? "less" : "note ›"}
          </button>
        )}
      </div>
      {noteLead && (
        <div id={noteId} style={styles.anchorBody}>
          <div style={styles.anchorSummary}>
            {note?.ai !== false && <AiMark />}
            {noteLead}
            {noteShown && noteRest ? ` ${noteRest}` : ""}
          </div>
          {noteShown && <SourceChips sources={noteSources} keyPrefix="lv" {...chips} />}
        </div>
      )}
    </div>
  ) : null;

  // ── Since the last visit: every event by day, newest first ──
  const anchorKey = dayKey(anchor?.date || lastVisit?.date);
  // Dated before the anchor or not dated at all: older care that arrived since.
  const isSince = ({ event }: EventRow) => {
    const day = dayKey(event.date);
    return !!day && day >= anchorKey;
  };
  const sinceDays = byDay(rows.filter(isSince));
  const beforeDays = byDay(rows.filter((row) => !isSince(row)));
  const showAllDays = wide || allDays;
  // The preview spends its three days on days with something besides folds
  // (administrative paperwork, older panels); a fold inside such a day still shows.
  const previewDays = sinceDays
    .filter((group) => group.rows.some(({ event }) => event.kind !== "fold"))
    .slice(0, PREVIEW_DAYS);
  const shownDays = showAllDays ? sinceDays : previewDays;
  const hiddenDays = sinceDays.length - previewDays.length;
  const canExpand = !wide && (hiddenDays > 0 || beforeDays.length > 0);
  const timelineId = `${idPrefix}-timeline`;

  const timelineTitle = !anchorKey
    ? "Last 365 days"
    : yourVisit
      ? `Since your ${dayLabel(anchorKey)} visit`
      : "Since the last visit";

  const renderEvent = ({ event, index }: EventRow) => {
    const key = `e${index}`;
    const muted = event.kind === "fold" || event.kind === "pending";
    const tags = [
      event.identity_mismatch && (
        <Tag key="identity" tone={styles.tagUnreviewed}>
          Check patient
        </Tag>
      ),
      event.unreviewed && (
        <Tag key="unreviewed" tone={styles.tagUnreviewed}>
          Unreviewed
        </Tag>
      ),
      (event.kind === "other_visit" || event.other_provider) && (
        <Tag key="other" tone={styles.tagTeal}>
          Another provider
        </Tag>
      ),
      OUTSIDE_KINDS.includes(event.kind) && (
        <Tag key="prisma" tone={styles.tagTeal}>
          Prisma
        </Tag>
      ),
    ].filter(Boolean);
    // A label-only chip repeats the row's title; keep the ones that open a detail.
    const expandable = withLabel(event.sources).filter((s) => !!s.detail?.trim());
    return (
      <li key={key} data-kind={event.kind}>
        <OpenableRow
          rowKey={key}
          rowStyle={{ ...styles.eventRow, ...(event.flagged ? styles.eventHigh : muted ? styles.eventMuted : {}) }}
          indent={EVENT_TEXT_INDENT}
          sources={expandable}
          sub={pretty(subLine(event))}
          chips={chips}
          onOpen={toggleRow}
        >
          {event.identity_mismatch ? (
            <span title="Identity check failed">
              <Glyph name="warning" />
              <span style={styles.srOnly}>Identity check failed: </span>
            </span>
          ) : (
            <Glyph name={KIND_GLYPH[event.kind] ?? "document"} />
          )}
          <span style={styles.rowText}>
            {event.flagged && <span style={styles.srOnly}>Flagged: </span>}
            {pretty(event.title)}
            <Tags tags={tags} />
          </span>
        </OpenableRow>
      </li>
    );
  };

  const renderDay = (group: DayGroup, older: boolean) => (
    <li key={group.day || "undated"} style={styles.day} data-day={group.day || "undated"}>
      <DateCell
        lines={dateLines(group.day, referenceYear)}
        style={{ ...styles.dayDate, ...(older ? styles.dayDateOlder : {}) }}
      />
      <ul style={styles.dayRows}>{group.rows.map(renderEvent)}</ul>
    </li>
  );

  const timeline =
    lastVisit || Array.isArray(data.events) ? (
      <div style={styles.section}>
        <div style={styles.sectionTitle}>{timelineTitle}</div>
        {anchorRow}
        {rows.length > 0 && (
          <div id={timelineId} style={styles.timeline}>
            {shownDays.length > 0 && <ol style={styles.days}>{shownDays.map((g) => renderDay(g, false))}</ol>}
            {showAllDays && beforeDays.length > 0 && (
              <div style={styles.before} data-group="before">
                <div style={styles.beforeLabel}>{anchorKey ? "Before the last visit, arrived since" : "Undated"}</div>
                <ol style={styles.days}>{beforeDays.map((g) => renderDay(g, true))}</ol>
              </div>
            )}
          </div>
        )}
        {Array.isArray(data.events) && data.events.length === 0 && (
          <div style={styles.quietLine}>Nothing new found in the records read.</div>
        )}
        {canExpand && (
          <button
            type="button"
            style={styles.seeMore}
            aria-expanded={allDays}
            aria-controls={timelineId}
            onClick={() => setAllDays((open) => !open)}
          >
            {allDays
              ? "Show less"
              : hiddenDays > 0
                ? `See ${hiddenDays} more ${hiddenDays === 1 ? "day" : "days"}`
                : "See more"}
          </button>
        )}
      </div>
    ) : null;

  // ── Worth asking: the model's questions, after the timeline ──
  const questions = (data.questions ?? []).filter((q) => !!q?.text?.trim()).slice(0, MAX_QUESTIONS);
  const worthAsking = questions.length ? (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>
        Worth asking
        <AiMark style={styles.aiMarkInTitle} />
      </div>
      <ul style={styles.questionList}>
        {questions.map((q, i) => (
          <li key={`q${i}`}>
            <OpenableRow
              rowKey={`q${i}`}
              rowStyle={styles.question}
              indent={QUESTION_TEXT_INDENT}
              sources={withLabel(q.sources)}
              badges
              chips={chips}
              onOpen={toggleRow}
            >
              <Glyph name="ask" />
              <span style={styles.rowText}>{pretty(q.text)}</span>
            </OpenableRow>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  // A payload from before the timeline (no events, flags or last visit) has
  // only its headline to say; every later payload leaves it to Superdoc.
  const fallbackHeadline =
    !data.flags && !data.last_visit && !Array.isArray(data.events) ? pretty(data.headline).trim() : "";

  const coverageGaps = (data.coverage?.gaps ?? []).filter((g) => !!g?.trim());
  const incomplete = data.coverage?.complete === false;
  const coverageText = pretty(coverageGaps.length ? coverageGaps.join("; ") : "some sources");

  // ── Actions ──
  const onRefresh = async () => {
    if (!clientId || frozen || refresh === "sending") return;
    setRefresh("sending");
    setRefresh(await requestPrechartRefresh(prechartBaseUrl(), clientId));
  };

  const onFeedback = async (helpful: boolean) => {
    if (frozen || feedback.phase === "sending" || feedback.phase === "sent") return;
    setFeedback({ phase: "sending", helpful });
    const ok = await sendPrechartFeedback(prechartBaseUrl(), data.brief_id, helpful, clientId);
    setFeedback(ok ? { phase: "sent", helpful } : { phase: "error" });
  };

  // Only a failed request can be retried; queued / running / turned off stay put.
  const refreshLocked = refresh !== "idle" && refresh !== "error";
  const refreshLabel =
    refresh === "sending" ? "Refreshing…" : refresh === "queued" || refresh === "in_progress" ? "Queued" : "Refresh";
  const refreshMessage =
    refresh === "queued"
      ? "Refresh queued. The updated brief shows the next time you open this patient."
      : refresh === "in_progress"
        ? "A refresh is already running for this visit."
        : refresh === "disabled"
          ? "Pre-charting is turned off for you."
          : refresh === "error"
            ? "Couldn't queue a refresh. Try again."
            : "";
  const feedbackLocked = feedback.phase === "sending" || feedback.phase === "sent";
  const feedbackMessage =
    feedback.phase === "sent"
      ? "Thanks for the feedback."
      : feedback.phase === "error"
        ? "Couldn't send feedback. Try again."
        : "";

  // Two columns only when there is something for the left one.
  const columns = wide && !!(quickSummary || worthAsking);

  return (
    <section
      ref={setCard}
      style={{ ...styles.container, ...(wide ? styles.containerWide : {}), ...(frozen ? styles.frozenBox : {}) }}
      aria-label="Pre-visit brief"
      data-layout={columns ? "columns" : "single"}
    >
      <div style={styles.header}>
        <div style={{ ...styles.iconBadge, ...(frozen ? styles.iconBadgeFrozen : {}) }}>
          <ClipboardIcon />
        </div>
        <div style={styles.headerText}>
          <span style={styles.title}>Pre-visit brief</span>
          {apptLine && <span style={styles.meta}>{apptLine}</span>}
          {lastVisitLine && <span style={styles.meta}>{lastVisitLine}</span>}
          {!aiComposed && <span style={styles.plainNote}>Plain listing (AI unavailable)</span>}
        </div>
      </div>

      {frozen && <div style={styles.frozenCaption}>An updated brief is further down the thread.</div>}

      {fallbackHeadline && (
        <div style={styles.headline}>
          {aiComposed && <AiMark />}
          {fallbackHeadline}
        </div>
      )}

      {columns ? (
        <div style={styles.columns}>
          <div style={styles.columnLeft} data-column="left">
            {quickSummary}
            {worthAsking}
          </div>
          <div style={styles.columnRight} data-column="right">
            {timeline}
          </div>
        </div>
      ) : (
        <>
          {quickSummary}
          {timeline}
          {worthAsking}
        </>
      )}

      {incomplete && (
        <div style={styles.coverage}>
          <b style={styles.coverageLabel}>Not read:</b> {coverageText}
          {coverageText.endsWith(".") ? "" : "."}
        </div>
      )}

      {!frozen && (
        <div style={styles.actions}>
          {clientId ? (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshLocked}
              style={{ ...styles.ghostButton, ...(refreshLocked ? styles.buttonDisabled : {}) }}
            >
              <ReloadOutlined spin={refresh === "sending"} style={{ fontSize: 12 }} />
              {refreshLabel}
            </button>
          ) : (
            <span />
          )}
          <div style={styles.feedback}>
            <button
              type="button"
              aria-label="Helpful"
              title="Helpful"
              aria-pressed={feedback.helpful === true && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(true)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === true && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== true ? styles.buttonDisabled : {}),
              }}
            >
              <LikeOutlined aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Not helpful"
              title="Not helpful"
              aria-pressed={feedback.helpful === false && feedback.phase !== "error"}
              disabled={feedbackLocked}
              onClick={() => onFeedback(false)}
              style={{
                ...styles.thumb,
                ...(feedback.helpful === false && feedbackLocked ? styles.thumbPicked : {}),
                ...(feedbackLocked && feedback.helpful !== false ? styles.buttonDisabled : {}),
              }}
            >
              <DislikeOutlined aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {!frozen && (refreshMessage || feedbackMessage) && (
        <div style={styles.status} role="status">
          {[refreshMessage, feedbackMessage].filter(Boolean).join(" ")}
        </div>
      )}
    </section>
  );
};

const listReset: React.CSSProperties = { listStyle: "none", margin: 0, padding: 0 };
const linkButton: React.CSSProperties = {
  padding: 0,
  border: 0,
  backgroundColor: "transparent",
  fontFamily: "inherit",
  fontWeight: 500,
  color: "#4338CA",
  cursor: "pointer",
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    padding: "16px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  containerWide: { padding: "20px 22px", gap: "20px" },
  frozenBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none" },
  frozenCaption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  iconBadgeFrozen: { backgroundColor: "#F3F4F6", filter: "grayscale(1)" },
  headerText: { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0, paddingTop: "3px" },
  title: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45 },
  meta: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
  plainNote: { fontSize: "11.5px", fontStyle: "italic", color: "#9CA3AF", lineHeight: 1.4 },

  headline: { fontSize: "13px", fontWeight: 500, color: "#111827", lineHeight: 1.5 },
  aiMark: { display: "inline-flex", verticalAlign: "-1px", marginRight: "5px", color: "#6366F1" },
  aiMarkInTitle: { marginRight: 0 },

  // Sections
  section: { display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11.5px",
    fontWeight: 600,
    color: "#6B7280",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  quietLine: { fontSize: "12.5px", color: "#6B7280", lineHeight: 1.45 },
  summaryLine: { fontSize: "13.5px", color: "#111827", lineHeight: 1.5, overflowWrap: "anywhere" },
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  glyph: { display: "block", width: "14px", height: "14px", marginTop: "3px", flexShrink: 0 },
  rowText: { minWidth: 0, overflowWrap: "anywhere" },
  tags: {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: "4px",
    marginLeft: "6px",
    verticalAlign: "1px",
  },
  tag: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "2px 6px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
  },
  tagUnreviewed: { color: "#B45309", backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" },
  tagTeal: { color: "#0F766E", backgroundColor: "#F0FDFA", border: "1px solid #99F6E4" },

  // Rows that open their evidence
  rowButton: {
    display: "block",
    width: "100%",
    padding: 0,
    margin: 0,
    border: 0,
    borderRadius: "6px",
    backgroundColor: "transparent",
    font: "inherit",
    color: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
  rowOpen: { display: "flex", flexDirection: "column", gap: "4px", paddingTop: "6px", minWidth: 0 },
  rowSub: { fontSize: "11.5px", color: "#6B7280", lineHeight: 1.4, overflowWrap: "anywhere" },

  // Quick summary: date | dot | glyph | text
  flagList: { ...listReset, display: "flex", flexDirection: "column", gap: "9px" },
  flagRow: {
    display: "grid",
    gridTemplateColumns: "44px 12px 14px minmax(0, 1fr)",
    columnGap: "6px",
    alignItems: "start",
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#374151",
  },
  flagDate: {
    display: "block",
    fontSize: "12px",
    color: "#6B7280",
    fontVariantNumeric: "tabular-nums",
    paddingTop: "1px",
    whiteSpace: "nowrap",
  },
  flagDotCell: { display: "flex", justifyContent: "center", paddingTop: "7px" },
  flagDot: {
    display: "block",
    boxSizing: "border-box",
    width: "7px",
    height: "7px",
    borderRadius: "999px",
    backgroundColor: "#D1D5DB",
  },
  flagDotHigh: { backgroundColor: "#DC2626" },
  flagDotOpen: {
    width: "8px",
    height: "8px",
    marginTop: "-1px",
    backgroundColor: "#FFFFFF",
    border: "2px solid #B45309",
  },
  flagHeadline: { fontWeight: 600, color: "#111827" },
  moreFlags: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4, paddingLeft: FLAG_TEXT_INDENT },
  identityList: { ...listReset, display: "flex", flexDirection: "column", gap: "6px" },
  identityLine: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    columnGap: "6px",
    alignItems: "start",
    paddingLeft: FLAG_GLYPH_INDENT,
    fontSize: "12px",
    color: "#6B7280",
    lineHeight: 1.5,
  },

  // The anchor row: the last visit, a light indigo box
  anchor: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "8px 10px",
    borderRadius: "9px",
    backgroundColor: "#F8F9FF",
    border: "1px solid #E0E7FF",
  },
  anchorHead: { fontSize: "12.5px", lineHeight: 1.5, color: "#374151", overflowWrap: "anywhere" },
  anchorDate: { fontWeight: 600, color: "#4338CA", marginRight: "6px" },
  anchorTitle: { fontWeight: 600, color: "#4338CA" },
  anchorTail: { color: "#4B5563" },
  anchorToggle: { ...linkButton, marginLeft: "6px", fontSize: "12px", lineHeight: "inherit" },
  anchorBody: { display: "flex", flexDirection: "column", gap: "6px" },
  anchorSummary: { fontSize: "12.5px", color: "#374151", lineHeight: 1.5 },

  // The timeline: date | rule | rows
  timeline: { display: "flex", flexDirection: "column", gap: "14px" },
  days: { ...listReset, display: "flex", flexDirection: "column", gap: "14px" },
  day: { display: "grid", gridTemplateColumns: "52px minmax(0, 1fr)", columnGap: "10px" },
  dayDate: {
    display: "block",
    fontSize: "12px",
    fontWeight: 600,
    color: "#4B5563",
    lineHeight: 1.5,
    paddingTop: "1px",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  dayDateOlder: { color: "#9CA3AF" },
  dayRows: {
    ...listReset,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
    borderLeft: "2px solid #F3F4F6",
    paddingLeft: "12px",
  },
  eventRow: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    columnGap: "7px",
    alignItems: "start",
    fontSize: "13px",
    color: "#374151",
    lineHeight: 1.5,
  },
  eventHigh: { fontWeight: 600, color: "#111827" },
  eventMuted: { color: "#6B7280" },
  before: { display: "flex", flexDirection: "column", gap: "10px" },
  beforeLabel: {
    fontSize: "11px",
    color: "#9CA3AF",
    lineHeight: 1.4,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  seeMore: { ...linkButton, alignSelf: "flex-start", margin: "2px 0 0 62px", fontSize: "12.5px", lineHeight: 1.4 },

  // Worth asking
  questionList: { ...listReset, display: "flex", flexDirection: "column", gap: "8px" },
  question: {
    display: "grid",
    gridTemplateColumns: "14px minmax(0, 1fr)",
    columnGap: "8px",
    alignItems: "start",
    fontSize: "12.5px",
    color: "#4B5563",
    lineHeight: 1.5,
  },

  // The wide layout
  columns: { display: "grid", gridTemplateColumns: "320px minmax(0, 1fr)", columnGap: "32px", alignItems: "start" },
  columnLeft: { display: "flex", flexDirection: "column", gap: "20px", minWidth: 0, position: "sticky", top: "16px" },
  columnRight: { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 },

  coverage: { fontSize: "11.5px", color: "#9CA3AF", lineHeight: 1.45, paddingTop: "4px" },
  coverageLabel: { fontWeight: 600, color: "#6B7280" },

  // Source chips
  chipRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: "2px 8px",
    borderRadius: "999px",
    // Longhands: the expanded state swaps only the colour.
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#E0E7FF",
    backgroundColor: "#F5F7FF",
    fontSize: "11.5px",
    fontWeight: 500,
    color: "#4338CA",
    lineHeight: 1.5,
    fontFamily: "inherit",
  },
  chipGroup: { display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: "4px", maxWidth: "100%" },
  chipButton: { cursor: "pointer" },
  chipExpanded: { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  chipLabel: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  detail: {
    fontSize: "12px",
    fontWeight: 400,
    color: "#4B5563",
    lineHeight: 1.45,
    padding: "6px 9px",
    borderRadius: "8px",
    backgroundColor: "#F9FAFB",
    borderLeft: "2px solid #C7D2FE",
    whiteSpace: "pre-wrap",
  },
  detailLabel: { fontWeight: 600, color: "#374151" },

  // Actions
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    borderTop: "1px solid #F0F1F5",
    paddingTop: "8px",
  },
  ghostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    fontSize: "12.5px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #D6D9E0",
    backgroundColor: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
  },
  feedback: { display: "flex", gap: "4px" },
  thumb: {
    width: "30px",
    height: "28px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    borderRadius: "8px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "transparent",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1,
  },
  thumbPicked: { borderColor: "#C7D2FE", backgroundColor: "#EEF2FF", cursor: "default" },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
  status: { fontSize: "12px", color: "#6B7280", lineHeight: 1.4 },
};

export default PreVisitBrief;
